/**
 * NotFound — 404 page
 * Dark Tech design: #0c0c0c bg, #57d7e2 cyan accent, Barlow font
 * Wrapped in SiteLayout for consistent nav/footer
 */
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

export default function NotFound() {
  useSeoMeta({
    title: "Page Not Found | Indexed",
    description:
      "The page you're looking for doesn't exist. It may have been moved or deleted.",
    noIndex: true,
  });

  return (
    <SiteLayout>
      <section
        style={{
          background: "#0c0c0c",
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "120px 24px 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle background glow */}
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "600px",
            background:
              "radial-gradient(circle, rgba(87,215,226,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ textAlign: "center", maxWidth: "560px", position: "relative", zIndex: 1 }}>
          {/* Large 404 number */}
          <h1
            style={{
              fontFamily: ff,
              fontSize: "clamp(100px, 15vw, 160px)",
              fontWeight: 800,
              lineHeight: 1,
              marginBottom: "8px",
              background: "linear-gradient(135deg, #57d7e2 0%, rgba(87,215,226,0.3) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-4px",
            }}
          >
            404
          </h1>

          {/* Subtitle */}
          <h2
            style={{
              fontFamily: ff,
              fontSize: "clamp(22px, 3vw, 28px)",
              fontWeight: 600,
              color: "white",
              marginBottom: "16px",
            }}
          >
            Page not found
          </h2>

          {/* Description */}
          <p
            style={{
              fontFamily: ff,
              fontSize: "16px",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.7,
              marginBottom: "40px",
              maxWidth: "440px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            The page you're looking for doesn't exist, has been moved, or was
            removed. Try one of the links below to get back on track.
          </p>

          {/* Navigation links */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
              marginBottom: "48px",
            }}
          >
            <Link
              href="/"
              style={{
                fontFamily: ff,
                fontSize: "15px",
                fontWeight: 600,
                color: "#0c0c0c",
                background: "#57d7e2",
                padding: "12px 28px",
                borderRadius: "6px",
                textDecoration: "none",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                ((e.target as HTMLElement).style.opacity = "0.85")
              }
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
                ((e.target as HTMLElement).style.opacity = "1")
              }
            >
              Go to Homepage
            </Link>
            <Link
              href="/services"
              style={{
                fontFamily: ff,
                fontSize: "15px",
                fontWeight: 600,
                color: "white",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: "12px 28px",
                borderRadius: "6px",
                textDecoration: "none",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                ((e.target as HTMLElement).style.borderColor = "rgba(255,255,255,0.4)")
              }
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
                ((e.target as HTMLElement).style.borderColor = "rgba(255,255,255,0.15)")
              }
            >
              View Services
            </Link>
          </div>

          {/* Helpful quick links */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {[
              { label: "Blog", href: "/blog" },
              { label: "Work", href: "/client-stories" },
              { label: "Industries", href: "/industries" },
              { label: "Talk to a Strategist", href: "/talk-to-a-strategist" },
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                style={{
                  fontFamily: ff,
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.4)",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  ((e.target as HTMLElement).style.color = "#57d7e2")
                }
                onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  ((e.target as HTMLElement).style.color = "rgba(255,255,255,0.4)")
                }
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
